import { useEffect, useCallback } from 'react';

import { abTestingService } from '../services/abTestingService';
import { analyticsService } from '../services/analyticsService';
import { feedbackService } from '../services/feedbackService';
import { freemiumLimitService } from '../services/freemiumLimitService';

export interface ConversionFunnelHook {
  trackAppInstall: () => Promise<void>;
  trackFirstQuiz: () => Promise<void>;
  trackThirdQuiz: () => Promise<void>;
  trackDailyLimitReached: () => Promise<void>;
  trackUpgradePromptShown: () => Promise<void>;
  trackUpgradePromptClicked: () => Promise<void>;
  trackSubscriptionPlanViewed: () => Promise<void>;
  trackSubscriptionStarted: (planId: string) => Promise<void>;
  trackSubscriptionCompleted: (planId: string, amount: number) => Promise<void>;
  trackQuizCompletion: (quizId: string, score: number) => Promise<void>;
}

/**
 * Hook for tracking user progress through the monetization conversion funnel
 * Based on Phase 2 funnel analysis requirements:
 *
 * App Install (100%) → First Quiz (85%) → Third Quiz (70%) → Daily Limit (50%) →
 * Upgrade Prompt (90%) → Plan View (25%) → Subscription Start (60%) → Complete (80%)
 * Target: 8.5% overall conversion rate
 */
export const useConversionFunnel = (userId?: string | null): ConversionFunnelHook => {
  
  /**
   * Track app install/first launch
   */
  const trackAppInstall = useCallback(async () => {
    try {
      // Track funnel step
      await analyticsService.trackFunnelStep('app_install', {
        timestamp: new Date().toISOString(),
        source: 'organic' // Can be enhanced with UTM tracking
      });

      // Track regular analytics event
      await analyticsService.track('app_installed', {
        platform: typeof window !== 'undefined' ? 'web' : 'mobile',
        timestamp: new Date().toISOString()
      });

      // Initialize user properties
      await analyticsService.setUserProperties({
        first_seen: new Date().toISOString(),
        platform: typeof window !== 'undefined' ? 'web' : 'mobile'
      });

    } catch (error) {
      console.error('Failed to track app install:', error);
    }
  }, []);

  /**
   * Track first quiz completion
   */
  const trackFirstQuiz = useCallback(async () => {
    try {
      await analyticsService.trackFunnelStep('first_quiz_completed', {
        timestamp: new Date().toISOString()
      });

      await analyticsService.track('first_quiz_completed', {
        milestone: 'onboarding',
        timestamp: new Date().toISOString()
      });

      // Check for upgrade prompt timing A/B test
      const promptTiming = await abTestingService.getUpgradePromptTiming();
      if (promptTiming === 'after_first') {
        await trackUpgradePromptShown();
      }

    } catch (error) {
      console.error('Failed to track first quiz:', error);
    }
  }, []);

  /**
   * Track third quiz completion (key engagement milestone)
   */
  const trackThirdQuiz = useCallback(async () => {
    try {
      await analyticsService.trackFunnelStep('third_quiz_completed', {
        timestamp: new Date().toISOString()
      });

      await analyticsService.track('third_quiz_completed', {
        milestone: 'engagement',
        timestamp: new Date().toISOString()
      });

      // Update user properties
      await analyticsService.setUserProperties({
        engagement_level: 'engaged',
        quiz_count: 3,
        last_active: new Date().toISOString()
      });

      // Check for upgrade prompt timing A/B test
      const promptTiming = await abTestingService.getUpgradePromptTiming();
      if (promptTiming === 'after_third') {
        await trackUpgradePromptShown();
      }

    } catch (error) {
      console.error('Failed to track third quiz:', error);
    }
  }, []);

  /**
   * Track when user hits daily limit (key conversion trigger)
   */
  const trackDailyLimitReached = useCallback(async () => {
    try {
      await analyticsService.trackFunnelStep('daily_limit_reached', {
        timestamp: new Date().toISOString(),
        limit: 5
      });

      await analyticsService.trackMonetizationEvent('quiz_limit_reached', {
        daily_limit: 5,
        timestamp: new Date().toISOString()
      });

      // Update user properties to mark as limit-reached
      await analyticsService.setUserProperties({
        reached_daily_limit: true,
        limit_reached_date: new Date().toISOString(),
        conversion_candidate: true
      });

      // Show upgrade prompt (unless A/B test shows earlier)
      const promptTiming = await abTestingService.getUpgradePromptTiming();
      if (promptTiming === 'after_limit') {
        await trackUpgradePromptShown();
      }

      // Check for feedback survey trigger
      const survey = await feedbackService.shouldShowSurvey('daily_limit_reached', {
        quiz_count: 5
      });

      if (survey) {
        await analyticsService.track('feedback_survey_triggered', {
          surveyId: survey.id,
          trigger: 'daily_limit_reached'
        });
      }

    } catch (error) {
      console.error('Failed to track daily limit reached:', error);
    }
  }, []);

  /**
   * Track upgrade prompt display
   */
  const trackUpgradePromptShown = useCallback(async () => {
    try {
      await analyticsService.trackFunnelStep('upgrade_prompt_viewed', {
        timestamp: new Date().toISOString()
      });

      await analyticsService.trackMonetizationEvent('upgrade_prompt_shown', {
        context: 'daily_limit_reached',
        timestamp: new Date().toISOString()
      });

      // Track A/B test conversion opportunity
      const promptTiming = await abTestingService.getUpgradePromptTiming();
      await abTestingService.trackConversion('upgrade_prompt_timing', 'prompt_shown');

    } catch (error) {
      console.error('Failed to track upgrade prompt shown:', error);
    }
  }, []);

  /**
   * Track upgrade prompt click (high-intent action)
   */
  const trackUpgradePromptClicked = useCallback(async () => {
    try {
      await analyticsService.trackFunnelStep('upgrade_prompt_clicked', {
        timestamp: new Date().toISOString()
      });

      await analyticsService.trackMonetizationEvent('upgrade_prompt_clicked', {
        intent: 'high',
        timestamp: new Date().toISOString()
      });

      // Update user properties to mark high intent
      await analyticsService.setUserProperties({
        upgrade_intent: 'high',
        last_upgrade_prompt_click: new Date().toISOString(),
        conversion_likelihood: 'high'
      });

      // Track A/B test conversion
      const promptTiming = await abTestingService.getUpgradePromptTiming();
      await abTestingService.trackConversion('upgrade_prompt_timing', 'prompt_clicked');

    } catch (error) {
      console.error('Failed to track upgrade prompt clicked:', error);
    }
  }, []);

  /**
   * Track subscription plans screen view
   */
  const trackSubscriptionPlanViewed = useCallback(async () => {
    try {
      await analyticsService.trackFunnelStep('subscription_plan_viewed', {
        timestamp: new Date().toISOString()
      });

      await analyticsService.track('subscription_plans_viewed', {
        intent: 'purchase_consideration',
        timestamp: new Date().toISOString()
      });

      // Get A/B test variant for plan display
      const planDisplay = await abTestingService.getSubscriptionPlanDisplay();
      await analyticsService.track('ab_test_plan_display', {
        variant: planDisplay,
        timestamp: new Date().toISOString()
      });

    } catch (error) {
      console.error('Failed to track subscription plan viewed:', error);
    }
  }, []);

  /**
   * Track subscription purchase initiation
   */
  const trackSubscriptionStarted = useCallback(async (planId: string) => {
    try {
      await analyticsService.trackFunnelStep('subscription_started', {
        planId,
        timestamp: new Date().toISOString()
      });

      await analyticsService.trackMonetizationEvent('subscription_started', {
        planId,
        funnel_stage: 'payment_initiation',
        timestamp: new Date().toISOString()
      });

      // Track A/B test conversions
      const planDisplay = await abTestingService.getSubscriptionPlanDisplay();
      await abTestingService.trackConversion('subscription_plan_display', 'subscription_started');
      
      const pricingStrategy = await abTestingService.getPricingStrategy();
      await abTestingService.trackConversion('pricing_strategy', 'subscription_started');

    } catch (error) {
      console.error('Failed to track subscription started:', error);
    }
  }, []);

  /**
   * Track successful subscription completion (final conversion)
   */
  const trackSubscriptionCompleted = useCallback(async (planId: string, amount: number) => {
    try {
      await analyticsService.trackFunnelStep('subscription_completed', {
        planId,
        amount,
        timestamp: new Date().toISOString()
      });

      await analyticsService.trackMonetizationEvent('subscription_completed', {
        planId,
        amount,
        revenue: amount,
        timestamp: new Date().toISOString()
      });

      // Update user properties to mark as converted
      await analyticsService.setUserProperties({
        subscription_status: 'active',
        subscription_plan: planId,
        subscription_start_date: new Date().toISOString(),
        lifetime_value: amount,
        customer_type: 'paid'
      });

      // Track A/B test final conversions with revenue
      const planDisplay = await abTestingService.getSubscriptionPlanDisplay();
      await abTestingService.trackConversion('subscription_plan_display', 'subscription_completed', amount);
      
      const pricingStrategy = await abTestingService.getPricingStrategy();
      await abTestingService.trackConversion('pricing_strategy', 'subscription_completed', amount);

      const promptTiming = await abTestingService.getUpgradePromptTiming();
      await abTestingService.trackConversion('upgrade_prompt_timing', 'subscription_completed', amount);

    } catch (error) {
      console.error('Failed to track subscription completed:', error);
    }
  }, []);

  /**
   * Track individual quiz completion (engagement tracking)
   */
  const trackQuizCompletion = useCallback(async (quizId: string, score: number) => {
    try {
      // Skip tracking if no user ID
      if (!userId) {
        console.log('Skipping quiz completion tracking - no user ID');
        return;
      }

      await analyticsService.trackUserBehavior('quiz_completed', 'game_play', {
        quizId,
        score,
        timestamp: new Date().toISOString()
      });

      // Get current daily usage with actual user ID
      const limits = await freemiumLimitService.getDailyUsage(userId);
      
      // Track milestone completions
      if (limits.currentUsage === 1) {
        await trackFirstQuiz();
      } else if (limits.currentUsage === 3) {
        await trackThirdQuiz();
      } else if (limits.currentUsage >= 5 && !limits.hasUnlimited) {
        await trackDailyLimitReached();
      }

      // Update engagement score
      await analyticsService.setUserProperties({
        total_quizzes: limits.currentUsage,
        last_quiz_score: score,
        last_active: new Date().toISOString(),
        engagement_score: Math.min(100, limits.currentUsage * 10) // Simple engagement scoring
      });

    } catch (error) {
      console.error('Failed to track quiz completion:', error);
    }
  }, [userId, trackFirstQuiz, trackThirdQuiz, trackDailyLimitReached]);

  return {
    trackAppInstall,
    trackFirstQuiz,
    trackThirdQuiz,
    trackDailyLimitReached,
    trackUpgradePromptShown,
    trackUpgradePromptClicked,
    trackSubscriptionPlanViewed,
    trackSubscriptionStarted,
    trackSubscriptionCompleted,
    trackQuizCompletion
  };
};

export default useConversionFunnel;